import GenericCard from "@ui-components/Card/GenericCard";
import React from "react";
import { ReactComponent as InternetIcon } from "../../../shared/assets/Icons/IconInternet.svg";
import { ReactComponent as PortIcon } from "../../../shared/assets/Icons/IconServer.svg";
import { ReactComponent as CloudIcon } from "../../../shared/assets/Icons/IconConnectivityKontrol.svg";
import { ReactComponent as DatacenterIcon } from "../../../shared/assets/Icons/IconDataInterconnect.svg";
import { ReactComponent as FwaasIcon } from "../../../shared/assets/Icons/IconFwaas.svg";
import { ReactComponent as SdwanIcon } from "../../../shared/assets/Icons/Icon_sdwan.svg";
import { ReactComponent as ArrowDown } from "../../../shared/assets/Icons/IconArrowDown.svg";
import { ReactComponent as ArrowUp } from "../../../shared/assets/Icons/IconArrowUp.svg";
import { ReactComponent as Warning } from "../../../shared/assets/Icons/Icon_Warning.svg";
import { useNavigate } from "react-router-dom";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import {
  useMaintenanceWindow,
  useFormatWindowStart,
} from "@shared/hooks/useMaintenanceStatus";

export type ServiceKind =
  | "internet"
  | "port"
  | "cloud"
  | "datacenter"
  | "fwaas"
  | "sdwan";

type ServiceStatusProps = {
  title: string;
  data: { id: string; status: string; subscriptionId: string }[];
  isLoading?: boolean;
  path?: string;
  showWarning?: boolean;
  service: ServiceKind;
  related?: ServiceKind[];
  relatedPresence?: Partial<Record<ServiceKind, number>>;
};

const SERVICE_ICON: Record<
  ServiceKind,
  React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
  internet: InternetIcon,
  port: PortIcon,
  cloud: CloudIcon,
  datacenter: DatacenterIcon,
  fwaas: FwaasIcon,
  sdwan: SdwanIcon,
};

// Helper: one cell for UP / WARNING / DOWN
function StatCell({
  count,
  label,
  Icon,
  classes,
  tooltipText,
  noData = false,
}: {
  count: number;
  label: string;
  Icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  classes: string;
  tooltipText?: string;
  /** Renders an em dash instead of a count. See NO_DATA below. */
  noData?: boolean;
}) {
  const content = (
    <div
      className={`
        flex flex-col items-center justify-center
        ${classes} font-medium
        px-2 py-2 xl:px-1 xl:py-1.5 2xl:px-2 2xl:py-2
        basis-0 grow min-w-0
        min-h-[50px] xl:min-h-[44px] 2xl:min-h-[54px]
      `}
    >
      <span className="text-l xl:text-sm 2xl:text-l font-bold leading-none">
        {noData ? "—" : count}
      </span>
      {/* Default: row [label][icon]. lg (1024-1280): col [label] above [icon]. xl+: col compact [icon] above [label]. */}
      <div className="mt-1 xl:mt-0.5 flex flex-row items-center gap-1 lg:flex-col lg:gap-0.5">
        <span className="xl:order-2 text-[11px] xl:text-[7px] 2xl:text-[9px] uppercase tracking-wide whitespace-nowrap leading-none">
          {label}
        </span>
        {Icon
          ? <Icon className="xl:order-1 w-4 h-4 xl:w-3 xl:h-3 shrink-0" />
          : <span className="xl:order-1 w-4 h-4 xl:w-3 xl:h-3 shrink-0" />
        }
      </div>
    </div>
  );

  return count > 0 && tooltipText ? (
    <GenericTooltip text={tooltipText} position="top">
      {content}
    </GenericTooltip>
  ) : (
    content
  );
}

export const ServiceStatusCard = ({
  title,
  data,
  isLoading = false,
  path,
  showWarning = false,
  service,
}: ServiceStatusProps) => {
  const navigate = useNavigate();
  const maintenance = useMaintenanceWindow("Kontrol Edge");
  const formatWindowStart = useFormatWindowStart();

  // classify
  const UP_STATUSES = new Set(["up", "active", "connected"]);
  const DOWN_STATUSES = new Set(["down", "inactive", "disconnected"]);
  const WARNING_STATUSES = new Set(["warning"]);

  const norm = (s?: string) => (s ?? "").toString().trim().toLowerCase();

  const upItems = data.filter((i) => UP_STATUSES.has(norm(i.status)));
  const downItems = data.filter((i) => DOWN_STATUSES.has(norm(i.status)));
  const warningItems = data.filter((i) => WARNING_STATUSES.has(norm(i.status)));

  const total = data.length;
  const upCount = upItems.length;
  const downCount = downItems.length;
  const warningCount = warningItems.length;

  /**
   * Every service came back unclassified — the backend sends 'unknown' when it
   * could not read the telemetry. Three states that used to look identical
   * here are now distinct: a tenant with no services (`total === 0`), services
   * we cannot see (this), and services that are genuinely down. Until CTX-544
   * the backend reported the middle one as 'down' and this card painted the
   * customer's healthy network red.
   */
  const hasNoTelemetry = total > 0 && upCount + downCount + warningCount === 0;
  const noDataSince =
    hasNoTelemetry && maintenance
      ? formatWindowStart(maintenance.since)
      : null;

  // tooltips (last 5)
  const upIdsToShow = upItems
    .slice(-5)
    .map((i) => i.subscriptionId)
    .join(", ");
  const downIdsToShow = downItems
    .slice(-5)
    .map((i) => i.subscriptionId)
    .join(", ");
  const warningIdsToShow = warningItems
    .slice(-5)
    .map((i) => i.subscriptionId)
    .join(", ");

  // colors
  const GRAY = "bg-semantic-bg-secondary text-semantic-text-secondary";
  const BRAND = "bg-semantic-bg-brand-secondary text-semantic-text-brand";
  const DANGER = "bg-semantic-bg-danger-secondary text-semantic-text-danger";
  const WARN = "bg-semantic-bg-warning-secondary text-semantic-text-warning";

  function getStatusClasses(count: number, kind: "up" | "down" | "warning") {
    // Nothing to colour when there is nothing to report: an em dash on a red
    // background would read as an outage.
    if (hasNoTelemetry) return GRAY;
    if (count <= 0) return GRAY;
    if (kind === "up") return BRAND;
    if (kind === "down") return DANGER;
    return WARN; // "warning"
  }

  const cols = showWarning ? "grid-cols-3" : "grid-cols-2";
  const MainIcon = SERVICE_ICON[service];

  return (
    <GenericCard
      isCollapsible={false}
      isLoading={isLoading}
      hoverable
      clickable={!!path}
      onClick={() => path && navigate(path)}
      className="h-full flex-1"
      bodyContent={
        <div className="flex flex-col flex-1 w-full py-1">
          <div className="flex items-start justify-start pb-2 xl:pb-1 2xl:pb-2 min-h-[3rem] xl:min-h-[2.25rem] 2xl:min-h-[3rem]">
            <div
              className="
              flex h-10 w-10 xl:h-8 xl:w-8 2xl:h-10 2xl:w-10 items-center justify-center
              rounded-sm
              border border-semantic-border-secondary
              bg-semantic-bg-secondary
              text-semantic-text-secondary
              shrink-0
            "
            >
              <MainIcon className="w-5 h-5 xl:w-4 xl:h-4 2xl:w-5 2xl:h-5" />
            </div>
            <div className="ml-3 xl:ml-2 2xl:ml-3">
              <h3 className="text-xs xl:text-[10px] 2xl:text-xs font-medium text-semantic-text-primary leading-tight line-clamp-2 max-h-[2.5rem] overflow-hidden">
                {title}
              </h3>
              <p className="text-sm xl:text-xs 2xl:text-sm font-medium text-semantic-text-secondary">
                Total: {total}
              </p>
            </div>
          </div>

          {/* Three cells row */}
          <div className="mt-auto pt-3 xl:pt-2 2xl:pt-3 relative">
            {/* xl+ (1280px): grid layout, compact at xl, roomier at 2xl */}
            <div
              className={`hidden xl:grid ${cols} items-stretch divide-x divide-gray-300`}
            >
              <StatCell
                count={upCount}
                label="UP"
                noData={hasNoTelemetry}
                Icon={ArrowUp}
                classes={getStatusClasses(upCount, "up")}
                tooltipText={
                  !hasNoTelemetry && upCount > 0
                    ? `Service ID: ${upIdsToShow}`
                    : undefined
                }
              />
              {showWarning && (
                <StatCell
                  count={warningCount}
                  label="WARNING"
                  noData={hasNoTelemetry}
                  Icon={Warning}
                  classes={getStatusClasses(warningCount, "warning")}
                  tooltipText={
                    warningCount > 0
                      ? `Service ID: ${warningIdsToShow}`
                      : undefined
                  }
                />
              )}
              <StatCell
                count={downCount}
                label="DOWN"
                noData={hasNoTelemetry}
                Icon={ArrowDown}
                classes={getStatusClasses(downCount, "down")}
                tooltipText={
                  downCount > 0 ? `Service ID: ${downIdsToShow}` : undefined
                }
              />
            </div>

            {/* Below xl: equal-width grid (each cell takes 1/N of the row) */}
            <div className={`xl:hidden grid ${cols} items-stretch`}>
              <StatCell
                count={upCount}
                label="UP"
                noData={hasNoTelemetry}
                Icon={ArrowUp}
                classes={getStatusClasses(upCount, "up")}
                tooltipText={
                  !hasNoTelemetry && upCount > 0
                    ? `Service ID: ${upIdsToShow}`
                    : undefined
                }
              />
              {showWarning && (
                <StatCell
                  count={warningCount}
                  label="WARNING"
                  noData={hasNoTelemetry}
                  Icon={Warning}
                  classes={getStatusClasses(warningCount, "warning")}
                  tooltipText={
                    warningCount > 0
                      ? `Service ID: ${warningIdsToShow}`
                      : undefined
                  }
                />
              )}
              <StatCell
                count={downCount}
                label="DOWN"
                noData={hasNoTelemetry}
                Icon={ArrowDown}
                classes={getStatusClasses(downCount, "down")}
                tooltipText={
                  downCount > 0 ? `Service ID: ${downIdsToShow}` : undefined
                }
              />
            </div>

            {hasNoTelemetry && (
              <p className="pt-2 text-center text-[10px] leading-none text-semantic-text-secondary">
                {noDataSince
                  ? `No data since ${noDataSince}`
                  : "No data available"}
              </p>
            )}
          </div>
        </div>
      }
    />
  );
};
